<template>
    <div class="main">
        <div class="row space-between">
            <div class="title">
                <h4>{{ teamStore.getTeamName }}</h4>
            </div>
            <div>
                <div v-for="(item, index) in state.story.tags" :key="index" class="tags">
                    <span>{{ item }}</span>
                </div>
            </div>
            <div class="buttons">
                <router-link :to="{ name: 'Home' }">
                    <button class="secondary-button" title="Back">
                        <font-awesome-icon :icon="['fas', 'arrow-left']" />
                    </button>
                </router-link>
                <router-link :to="{ name: 'Edit' }">
                    <button class="action-button" title="Edit"><font-awesome-icon :icon="['fas', 'edit']" /></button>
                </router-link>
            </div>
        </div>
        <div class="detail">
            <div v-if="!state.loading">
                <h3>{{ state.story.heading }}</h3>
            </div>
            <div v-if="state.loading" class="text-editor">
                <TextEditorSkeleton />
            </div>
            <div v-else class="text-editor" v-html="sanitizeHtml(state.story.body)" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { TeamStory } from "@/services/entities/teamStory";
import { useTeamStoryStore } from "@/stores/teamStory";
import { onMounted, reactive } from "vue";
import DOMPurify from "dompurify";
import { useTeamStore } from "@/stores/team";
import TextEditorSkeleton from "@/components/util/TextEditorSkeleton.vue";

const teamStoryStore = useTeamStoryStore();
const teamStore = useTeamStore();

const { id } = defineProps<{
    id: string;
}>();

const state = reactive({
    saved: false,
    loading: true,
    story: { heading: "", body: "" } as any as TeamStory,
});

function sanitizeHtml(html: string) {
    return DOMPurify.sanitize(html, { ADD_ATTR: ["target"] });
}

onMounted(async () => {
    if (id) {
        state.loading = true;
        if (teamStoryStore.getTeamStories.length === 0) {
            await teamStoryStore.loadRecord(id);
        }
        const result = teamStoryStore.getTeamStories.find((story) => story.id?.toLowerCase() === id?.toLowerCase());
        if (result) {
            state.story = result;
        }
        state.loading = false;
    }
});
</script>

<style lang="scss" scoped>
.tags {
    display: inline-flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    margin: 0.25rem;
    border-radius: 1rem;
    background-color: var(--color-primary);
    color: var(--color-text-white);
    font-size: 0.875rem;
    font-weight: 500;
    transition: all 0.2s ease;

    span {
        line-height: 1;
    }

    &:hover {
        background-color: var(--color-primary-hover);
        transform: scale(1.02);
    }
}
.main {
    display: flex;
    flex-direction: column;
    padding-top: 30px;
}

.buttons {
    display: flex;
    gap: 10px;
    padding-right: 3px;
}

.text-editor {
    margin-top: 5px;
    width: 100%;
    height: 100%;
    padding: 1rem;
    padding-left: 1.5rem;
    background-color: #ffffff;
    color: #1f1f1f;
    border-radius: 0.25rem;

    /* Ensure all rich text content uses light theme colors */
    :deep(*) {
        color: inherit;
    }

    /* Ensure links are visible */
    :deep(a) {
        color: var(--color-info);
        text-decoration: underline;
    }

    /* Blockquote styling */
    :deep(blockquote) {
        border-left: 4px solid var(--color-border);
        margin: 0.5rem 0;
        padding-left: 1rem;
        font-style: italic;
        color: var(--color-text-muted);
    }

    /* Code block styling */
    :deep(pre.ql-syntax) {
        background: var(--color-bg-code);
        border: 1px solid var(--color-border);
        border-radius: 0.5rem;
        padding: 1rem;
        overflow-x: auto;
        margin: 0.5rem 0;
        font-family: "Courier New", Courier, monospace;
        font-size: 0.875rem;
        color: #f8f8f2;
    }
}

.row {
    display: flex;
    flex-direction: row;
    width: 100%;

    &.space-between {
        justify-content: space-between;
    }
}

.detail {
    display: flex;
    flex-direction: column;
    margin-top: 10px;
    margin-bottom: 10px;
    padding: 5px;
    border-radius: 3px;
    background-color: var(--color-bg-white);
}

.row-right {
    display: flex;
    margin-top: 10px;
    align-items: right;
    justify-content: right;
}

a {
    padding-left: 5px;
}
</style>
